// Generated by TaroBase SDK Generator: 2026-05-01T17:36:41.367Z
// Collection: balanceApplicationRequests/$requestId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * User intent to apply PDA balance toward a session. Offchain record created by the user (or backend on the user's behalf) declaring how much of which rail from which userPaymentAccount should be applied to a given session. Backend processes the request: validates session is pending and balance is sufficient, then writes a balanceSettlementActions doc to perform the onchain transfer to the merchant. status transitions: 'pending' -> 'applied' / 'rejected' / 'failed'.
 */

export interface BalanceApplicationRequestsRequest {
  sessionId: string;
  appid: string;
  payer: AddressType;
  userAccountId: string;
  rail: string;
  amount: number | TimeOperation | IncrementOperation | TokenAmount;
  status: string;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface BalanceApplicationRequestsResponse {
  sessionId: string;
  appid: string;
  payer: string;
  userAccountId: string;
  rail: string;
  amount: number;
  status: string;
  createdAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a BalanceApplicationRequests operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildBalanceApplicationRequests(requestId: string, data?: BalanceApplicationRequestsRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `balanceApplicationRequests/${requestId}`, document: doc };
}

/**
 * Either the payer themselves (@newData.payer == @user.address) records intent, or the backend (@constants.PROJECT_VAULT_ADDRESS) creates one on the user's behalf when the user signs an off-band intent. The backend is the only writer that can later transition status, so creating one as the user is purely a request — actual settlement happens in balanceSettlementActions, gated by backend validation. Required fields: sessionId, appid, payer, userAccountId, rail ('sol'|'usdc'|'usdt'), amount (raw base units), status ('pending' on create), createdAt. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setBalanceApplicationRequests(requestId: string, data?: BalanceApplicationRequestsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`balanceApplicationRequests/${requestId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting BalanceApplicationRequests: ${formatError(error)}`);
    return false;
  }
}

export type BalanceApplicationRequestsRequestUpdate = Partial<BalanceApplicationRequestsRequest>;

/**
 * Build a BalanceApplicationRequests update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateBalanceApplicationRequests(requestId: string, data: BalanceApplicationRequestsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `balanceApplicationRequests/${requestId}`, document: doc };
}

/**
 * Service-only (@constants.PROJECT_VAULT_ADDRESS). Backend transitions status as it validates the request, performs the onchain settlement, and reconciles results. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateBalanceApplicationRequests(requestId: string, data: BalanceApplicationRequestsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`balanceApplicationRequests/${requestId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating BalanceApplicationRequests: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Public read. Application requests reference the session, payer, and PDA but contain no sensitive data. Required for backend reconciliation and operator dashboards.
   (Get Single Item)
 */
export async function getBalanceApplicationRequests(requestId: string): Promise<BalanceApplicationRequestsResponse | null> {
  try {
    const result = await get(`balanceApplicationRequests/${requestId}`);
    return result as BalanceApplicationRequestsResponse | null;
  } catch (error) {
    console.error(`Error getting BalanceApplicationRequests: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single BalanceApplicationRequests document. (
  Read Operation Details: Public read. Application requests reference the session, payer, and PDA but contain no sensitive data. Required for backend reconciliation and operator dashboards.
  )
 */
export function subscribeBalanceApplicationRequests(callback: (data: BalanceApplicationRequestsResponse | null) => void, requestId: string): Promise<() => Promise<void>> {
  return subscribe(`balanceApplicationRequests/${requestId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as BalanceApplicationRequestsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to BalanceApplicationRequests at path '${`balanceApplicationRequests/${requestId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many BalanceApplicationRequests items from collection balanceApplicationRequests
 
  Read Operation Details: Public read. Application requests reference the session, payer, and PDA but contain no sensitive data. Required for backend reconciliation and operator dashboards.
  
 */
export async function getManyBalanceApplicationRequests(filter: string = ""): Promise<BalanceApplicationRequestsResponse[]> {
  try {
    const results = await get("balanceApplicationRequests", { prompt: filter });
    return Array.isArray(results) ? results as BalanceApplicationRequestsResponse[] : [];
  } catch (error) {
    console.error(`Error getting BalanceApplicationRequests collection at path 'balanceApplicationRequests': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in BalanceApplicationRequests collection at balanceApplicationRequests
 
  Read Operation Details: Public read. Application requests reference the session, payer, and PDA but contain no sensitive data. Required for backend reconciliation and operator dashboards.
  
 */
export function subscribeManyBalanceApplicationRequests(callback: (data: BalanceApplicationRequestsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("balanceApplicationRequests", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as BalanceApplicationRequestsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to BalanceApplicationRequests collection at path '${"balanceApplicationRequests"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all BalanceApplicationRequests items from collection balanceApplicationRequests
 
  Read Operation Details: Public read. Application requests reference the session, payer, and PDA but contain no sensitive data. Required for backend reconciliation and operator dashboards.
  
 */
export async function getAllBalanceApplicationRequests(filter: string = ""): Promise<BalanceApplicationRequestsResponse[]> {
  try {
    const results = await get("balanceApplicationRequests", { prompt: filter });
    return Array.isArray(results) ? results as BalanceApplicationRequestsResponse[] : [];
  } catch (error) {
    console.error(`Error getting BalanceApplicationRequests collection at path 'balanceApplicationRequests': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in BalanceApplicationRequests collection at balanceApplicationRequests
 
  Read Operation Details: Public read. Application requests reference the session, payer, and PDA but contain no sensitive data. Required for backend reconciliation and operator dashboards.
  
 */
export function subscribeAllBalanceApplicationRequests(callback: (data: BalanceApplicationRequestsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("balanceApplicationRequests", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as BalanceApplicationRequestsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to BalanceApplicationRequests collection at path '${"balanceApplicationRequests"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count BalanceApplicationRequests items in collection balanceApplicationRequests.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countBalanceApplicationRequests(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("balanceApplicationRequests", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting BalanceApplicationRequests at path 'balanceApplicationRequests': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on BalanceApplicationRequests items in collection balanceApplicationRequests.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateBalanceApplicationRequests(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("balanceApplicationRequests", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating BalanceApplicationRequests at path 'balanceApplicationRequests': ${formatError(error)}`);
    return { value: 0 };
  }
}

